import { DefaultError } from './errors/DefaultError'
import { UserLoginError } from './errors/UserLoginError'
import { UnverifiedEmailError } from './errors/UnverifiedEmailError'
import { ChangePasswordError } from './errors/ChangePasswordError'
import { CreateUserError } from './errors/CreateUserError'
import { DeleteUserError } from './errors/DeleteUserError'
import { GetUserError } from './errors/GetUserError'
import { FederatedUserLoginError } from './errors/FederatedUserLoginError'
import { VerifyUserError } from './errors/VerifyUserError'
import { FederatedUserInvitationInvalidToken } from './errors/FederatedUserInvitationInvalidToken'
import { FederatedUserInvitationAlreadyProcessed } from './errors/FederatedUserInvitationAlreadyProcessed'
import { FederatedUserInvitationExpired } from './errors/FederatedUserInvitationExpired'
import { FederatedUserInvitationServerError } from './errors/FederatedUserInvitationServerError'
import { FederatedUserInvitationUpdateFailed } from './errors/FederatedUserInvitationUpdateFailed'
import { FederatedUserInvitationEmailVerifyFailed } from './errors/FederatedUserInvitationEmailVerifyFailed'
import { FederatedUserUnauthorized } from './errors/FederatedUserUnauthorized'

/**
 * Registry mapping error codes to error components.
 * Multiple error codes can map to the same component for aliases.
 */
/**
 * Decodes base64url encoded string (URL-safe base64 without padding).
 * Reverses the encoding done by generateNonce function in FederatedLogin.
 *
 * @private
 * @param {string} encoded - Base64url encoded string
 * @returns {string} Decoded string
 * @throws {Error} If input is invalid base64url
 *
 * @example
 * // Decode base64url string
 * const decoded = decodeBase64Url('eyJjb25uZWN0aW9uIjoiZ29vZ2xlLW9hdXRoMiIsImV4dGVybmFsSWQiOiIxMjM0NTYifQ')
 * // Returns: '{"connection":"google-oauth2","externalId":"123456"}'
 *
 * @example
 * // Handles URL-safe characters
 * const decoded = decodeBase64Url('aGVsbG8td29ybGRfd2l0aF9zcGVjaWFsX2NoYXJz')
 * // Returns: 'hello-world_with_special_chars'
 */
function decodeBase64Url(encoded) {
  if (!encoded || typeof encoded !== 'string') {
    throw new Error('Invalid base64url input')
  }

  // Convert base64url to standard base64
  // Replace URL-safe characters with standard base64 characters
  let base64 = encoded
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  // Add padding if needed (base64 requires length to be multiple of 4)
  const padding = (4 - (base64.length % 4)) % 4
  base64 += '='.repeat(padding)

  try {
    return atob(base64)
  } catch (error) {
    throw new Error('Failed to decode base64url: invalid format')
  }
}

const ERROR_REGISTRY = {
  // Default fallback
  _DEFAULT_: DefaultError,

  // User login errors (multiple codes → same component)
  user_not_registered: UserLoginError,
  login_error: UserLoginError,
  login_failed: UserLoginError,

  // Federated login errors
  federated_user_login_error: FederatedUserLoginError,
  federated_user_login_failed: FederatedUserLoginError,

  // Unverified email
  unverified_email: UnverifiedEmailError,

  // Password change errors
  change_password_error: ChangePasswordError,
  change_password_failed: ChangePasswordError,

  // User creation errors
  create_user_error: CreateUserError,
  create_user_failed: CreateUserError,
  user_exists: CreateUserError,

  // User deletion errors
  delete_user_error: DeleteUserError,
  delete_user_failed: DeleteUserError,

  // Get user errors
  get_user_error: GetUserError,
  get_user_failed: GetUserError,

  // User verification errors
  verify_user_error: VerifyUserError,
  verify_user_failed: VerifyUserError,

  // Federated user invitation errors
  FEDERATED_USER_INVITATION_INVALID_TOKEN: FederatedUserInvitationInvalidToken,
  FEDERATED_USER_INVITATION_ALREADY_PROCESSED: FederatedUserInvitationAlreadyProcessed,
  FEDERATED_USER_INVITATION_EXPIRED: FederatedUserInvitationExpired,
  FEDERATED_USER_INVITATION_SERVER_ERROR: FederatedUserInvitationServerError,
  FEDERATED_USER_INVITATION_UPDATE_FAILED: FederatedUserInvitationUpdateFailed,
  FEDERATED_USER_INVITATION_EMAIL_VERIFY_FAILED: FederatedUserInvitationEmailVerifyFailed,
}

/**
 * Resolves error code and description to the appropriate error component.
 * Handles special case resolution logic for complex error patterns.
 *
 * @param {string} errorCode - Error code from query parameter
 * @param {string} errorDescription - Error description from query parameter
 * @returns {React.Component} Error component to render
 */
export function getErrorComponent(errorCode, errorDescription) {
  // Special case: description-based matching
  if (errorDescription === 'This user has not been registered yet.') {
    return ERROR_REGISTRY.user_not_registered
  }

  // Special case: combined error code + description
  if (errorCode === 'access_denied' && errorDescription === 'unverified_email') {
    return ERROR_REGISTRY.unverified_email
  }

  // Special case: federated_user_unauthorized with encoded data
  if (errorCode === 'access_denied' && errorDescription?.startsWith('federated_user_unauthorized::')) {
    return FederatedUserUnauthorized
  }

  // Return default for null/undefined error codes
  if (!errorCode) {
    return ERROR_REGISTRY._DEFAULT_
  }

  // Lookup component in registry, fallback to default
  return ERROR_REGISTRY[errorCode] || ERROR_REGISTRY._DEFAULT_
}

// Export decodeBase64Url for use in ErrorPage
export { decodeBase64Url }

// Export for testing only
export const __testExports__ = {
  decodeBase64Url
}
